import { useState, useMemo } from 'react';
import { Search, ChevronDown } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils/cn';
import { FadeIn, StaggerChildren, StaggerItem } from '@/components/motion';
import { faqItems } from '../data/faq-data';
import { FAQ_CATEGORIES } from '../types/help-center.types';
import type { FaqCategory } from '../types/help-center.types';

export function FaqSection() {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState<FaqCategory | 'all'>('all');
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const filteredItems = useMemo(() => {
    let items = faqItems;

    if (activeCategory !== 'all') {
      items = items.filter((item) => item.category === activeCategory);
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      items = items.filter(
        (item) =>
          item.question.toLowerCase().includes(query) || item.answer.toLowerCase().includes(query)
      );
    }

    return items;
  }, [searchQuery, activeCategory]);

  function toggleItem(id: string) {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  function getCategoryLabel(category: FaqCategory): string {
    return FAQ_CATEGORIES.find((c) => c.value === category)?.label ?? category;
  }

  return (
    <div className="space-y-6">
      {/* Search bar */}
      <FadeIn direction="up">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Buscar en preguntas frecuentes..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
      </FadeIn>

      {/* Category filter chips */}
      <FadeIn direction="up" delay={0.05}>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setActiveCategory('all')}
            className={cn(
              'inline-flex items-center rounded-full border px-3 py-1 text-xs font-medium transition-colors',
              activeCategory === 'all'
                ? 'border-primary bg-primary text-primary-foreground'
                : 'border-border bg-background text-muted-foreground hover:bg-muted'
            )}
          >
            Todas ({faqItems.length})
          </button>
          {FAQ_CATEGORIES.map((cat) => {
            const count = faqItems.filter((i) => i.category === cat.value).length;
            return (
              <button
                key={cat.value}
                onClick={() => setActiveCategory(cat.value)}
                className={cn(
                  'inline-flex items-center rounded-full border px-3 py-1 text-xs font-medium transition-colors',
                  activeCategory === cat.value
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-border bg-background text-muted-foreground hover:bg-muted'
                )}
              >
                {cat.label} ({count})
              </button>
            );
          })}
        </div>
      </FadeIn>

      {/* Result count */}
      <p className="text-sm text-muted-foreground">
        {filteredItems.length} {filteredItems.length === 1 ? 'resultado' : 'resultados'}
        {searchQuery.trim() && (
          <span>
            {' '}
            para &ldquo;<span className="font-medium text-foreground">{searchQuery}</span>&rdquo;
          </span>
        )}
      </p>

      {/* FAQ accordion */}
      <StaggerChildren className="space-y-2">
        {filteredItems.map((item) => {
          const isExpanded = expandedIds.has(item.id);
          return (
            <StaggerItem key={item.id}>
              <div className="rounded-lg border bg-card transition-shadow hover:shadow-sm">
                <button
                  onClick={() => toggleItem(item.id)}
                  className="flex w-full items-start justify-between gap-4 px-4 py-4 text-left"
                >
                  <div className="flex min-w-0 flex-1 items-start gap-3">
                    <span className="mt-0.5 shrink-0">
                      <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                        {getCategoryLabel(item.category)}
                      </Badge>
                    </span>
                    <span className="text-sm font-medium leading-snug">{item.question}</span>
                  </div>
                  <ChevronDown
                    className={cn(
                      'mt-0.5 h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200',
                      isExpanded && 'rotate-180'
                    )}
                  />
                </button>

                {/* Animated answer panel */}
                <div
                  className={cn(
                    'grid transition-all duration-200 ease-in-out',
                    isExpanded ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
                  )}
                >
                  <div className="overflow-hidden">
                    <div className="border-t px-4 pb-4 pt-3">
                      <p className="text-sm leading-relaxed text-muted-foreground">{item.answer}</p>
                    </div>
                  </div>
                </div>
              </div>
            </StaggerItem>
          );
        })}
      </StaggerChildren>

      {/* Empty state */}
      {filteredItems.length === 0 && (
        <FadeIn direction="up">
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Search className="mb-3 h-10 w-10 text-muted-foreground/40" />
            <p className="text-sm font-medium text-muted-foreground">
              No se encontraron resultados
            </p>
            <p className="mt-1 text-xs text-muted-foreground/70">
              Intenta con otros terminos de búsqueda o cambia la categoría
            </p>
          </div>
        </FadeIn>
      )}
    </div>
  );
}
